import { Fragment, useMemo, useRef } from 'react';
import { useTicketDetailsParams } from '../hooks/useTicketDetailsParams';
import styled from 'styled-components';
import { Flex } from 'foundations-components/components/Flex';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { ViewDetailsIcon } from 'foundations-assets/icons/components/ViewDetailsIcon';
import { Button } from 'foundations-components/transitional/components/Button';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { ThreadMessage } from './ThreadMessage';
import { DateDivider } from './DateDivider';
import { areDifferentDates } from '../utils/areDifferentDates';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { ThreadHistoryHeader } from './ThreadHistoryHeader';
import { AddPeoplePopover } from './AddPeoplePopover';
import { VENUS_LAYER } from 'foundations-components/transitional/constants';
import { useIsPreview } from '../hooks/useIsPreview';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const NewMessageIndicator = styled.div.withConfig({
  displayName: "ThreadHistory__NewMessageIndicator"
})(["position:absolute;bottom:20px;left:50%;transform:translateX(-50%);z-index:10;pointer-events:auto;opacity:0;animation:fadeInUp 0.3s ease-out forwards;cursor:pointer;@keyframes fadeInUp{from{opacity:0;transform:translateX(-50%) translateY(10px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}"]);
const StyledMessageThreadContainer = styled.div.withConfig({
  displayName: "ThreadHistory__StyledMessageThreadContainer"
})(["display:contents;& >:first-child{overscroll-behavior:contain;}"]);
export const ThreadHistory = ({
  threads,
  isCreatingThread,
  isLoading,
  hasMore,
  fetchMore,
  messages,
  isFetchingMore,
  responders,
  attachmentsMap,
  sensitiveAttachmentsMetadata,
  setIsPanelOpen,
  showOpenPanelButton
}) => {
  const {
    threadId
  } = useTicketDetailsParams();
  const isPreview = useIsPreview();
  const currentThread = useMemo(() => threads.find(thread => thread.threadId === threadId), [threads, threadId]);
  const showAddPeople = !isPreview && !isLoading && !isCreatingThread && Boolean(currentThread);
  const containerRef = useRef(null);
  const {
    scrollToBottom,
    hasNewItems
  } = useInfiniteScroll({
    containerRef,
    hasNextPage: hasMore,
    isFetchingNextPage: isFetchingMore,
    fetchNextPage: fetchMore,
    items: messages,
    resetKey: threadId
  });
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "row",
    gap: "200",
    inlineSize: "100%",
    blockSize: "100%",
    minBlockSize: "0"
    // @ts-ignore Flex color prop is not currently allowed
    ,
    color: "text-core-default",
    children: /*#__PURE__*/_jsxs(Flex, {
      flex: "1",
      maxInlineSize: "100%",
      blockSize: "100%",
      minBlockSize: "0",
      flexDirection: "column",
      position: "relative",
      children: [/*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        flexDirection: "row",
        justifyContent: "space-between",
        backgroundColor: "fill-surface-default",
        alignItems: "center",
        inlineSize: "100%",
        paddingBlock: "400",
        paddingBlockEnd: "0",
        paddingInline: "500",
        borderBlockEndWidth: "100",
        borderBlockEndStyle: "solid",
        borderColor: "border-core-subtle"
        // Must be set to border-core-subtle to match the border token used by default in the Tabs component
        ,
        children: [/*#__PURE__*/_jsx(ThreadHistoryHeader, {
          threads: threads
        }), showOpenPanelButton && /*#__PURE__*/_jsx(IconButton, {
          use: "unstyled",
          onClick: () => setIsPanelOpen(true),
          tooltip: I18n.text('customer-portal-app.ticketInfoPanel.open'),
          "aria-label": I18n.text('customer-portal-app.ticketInfoPanel.open')
          // @ts-ignore IconButton color prop is not currently allowed
          ,
          color: "icon-core-default",
          children: /*#__PURE__*/_jsx(ViewDetailsIcon, {
            size: "800"
          })
        })]
      }), showAddPeople && /*#__PURE__*/_jsx(Flex, {
        position: "relative",
        blockSize: "0",
        zIndex: VENUS_LAYER,
        children: /*#__PURE__*/_jsx(Flex, {
          position: "absolute",
          insetBlockStart: "12px",
          insetInlineEnd: "24px",
          children: /*#__PURE__*/_jsx(AddPeoplePopover, {
            thread: currentThread
          })
        })
      }), /*#__PURE__*/_jsx(StyledMessageThreadContainer, {
        children: /*#__PURE__*/_jsx(TFlex, {
          ref: containerRef,
          id: "thread-history",
          color: "text-core-default",
          padding: "600",
          flexDirection: "column-reverse",
          alignItems: "stretch",
          blockSize: "0",
          minBlockSize: "0",
          overflowY: "auto",
          gap: 28,
          children: isCreatingThread || isLoading ? /*#__PURE__*/_jsxs(Flex, {
            alignSelf: "center",
            justifySelf: "center",
            blockSize: "100%",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "200",
            children: [/*#__PURE__*/_jsx(LoadingSpinner, {
              size: "extra-small",
              "aria-label": I18n.text('customer-portal-app.loading')
            }), isCreatingThread && /*#__PURE__*/_jsx(FormattedMessage, {
              message: "customer-portal-app.ticketThread.creatingThread"
            })]
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [[...messages].reverse().map((message, index, reversedArray) => {
              const olderMessage = index < reversedArray.length - 1 ? reversedArray[index + 1] : null;
              const showDateDivider = !olderMessage || areDifferentDates(message.timestamp, olderMessage.timestamp);
              return /*#__PURE__*/_jsxs(Fragment, {
                children: [/*#__PURE__*/_jsx(ThreadMessage, {
                  message: message,
                  attachmentsMap: attachmentsMap,
                  sensitiveAttachmentsMetadata: sensitiveAttachmentsMetadata,
                  responders: responders
                }), showDateDivider && /*#__PURE__*/_jsx(DateDivider, {
                  timestamp: message.timestamp
                })]
              }, message.id);
            }), hasMore && /*#__PURE__*/_jsx(Flex, {
              blockSize: "100%",
              justifyContent: "center",
              children: /*#__PURE__*/_jsx(Badge, {
                use: "koala",
                children: /*#__PURE__*/_jsxs(Flex, {
                  display: "flex",
                  flexDirection: "row",
                  gap: "100",
                  alignItems: "center",
                  children: [/*#__PURE__*/_jsx(LoadingSpinner, {
                    size: "extra-small",
                    marginInline: "0",
                    "aria-label": I18n.text('customer-portal-app.loading')
                  }), /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "customer-portal-app.loading"
                  })]
                })
              })
            })]
          })
        })
      }), hasNewItems && /*#__PURE__*/_jsx(NewMessageIndicator, {
        children: /*#__PURE__*/_jsxs(Button, {
          use: "primary",
          size: "extra-small",
          onClick: () => scrollToBottom({
            smooth: true
          }),
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketThread.messageContent.newMessage"
          }), /*#__PURE__*/_jsx(DownIcon, {
            size: "100"
          })]
        })
      })]
    })
  });
};